import React, {useContext, useMemo} from "react";
import {T, useTranslate} from "@tolgee/react";
import {CardOrder, OrderProduct as IOrderProduct} from "ts/api/order";
import FlexBox from "view/components/Layout/FlexBox";
import Avatar from "view/components/Avatar";
import {Typography} from "@material-ui/core";
import ElipsisWrapper from "view/components/DataRow/ElipsisWrapper";
import {CurrencyContext} from "view/context/CurrencyContext";
import formatPrice from "tools/formats/formatPrice";
import clsx from "clsx";
import TooltipIcon from "view/components/TooltipIcon";
import ImportantIcon from "assets/ImportantIcon";
import {Currency} from "@evennt-tech/api-v2-core";
import {grey} from "@material-ui/core/colors";
import {
  getCustomCpoString,
  getExtrasString,
} from "view/apps/Exhibitor/pages/Cart/components/Payment/components/Card/OrdersList/CartOrder/ProductList/helpers";
import {useProductConfigs} from "store/custom-product-option/selectors";
import {useLocalizedNameTranslate} from "hooks/useLocalizedNameTranslate";
import {resolveLocalizedConfig} from "tools/localization/resolveLocalizedProduct";
import useStyles from "./styles";

const ProductList = ({order}: {order?: CardOrder}) => {
  if (!order?.products?.length) {
    return null;
  }
  return (
    <FlexBox column gap={0} fullWidth>
      {order.products.map((product, idx) => (
        <OrderProduct key={product.id} product={product} index={idx} />
      ))}
    </FlexBox>
  );
};

const OrderProduct = ({
  product,
  index,
}: {
  product: IOrderProduct;
  index: number;
}) => {
  const classes = useStyles();
  const {t} = useTranslate();
  const currency = useContext(CurrencyContext);
  const localizedT = useLocalizedNameTranslate();
  const productConfigs = useProductConfigs(product.owner.id, product.id);
  const {image, priceWithFees, quantity, name, approval} = product;

  const selectedConfig = useMemo(() => {
    if (!productConfigs?.length) {
      return null;
    }

    if (!product.cpoConfig?.isCustom) {
      return null;
    }
    const config = productConfigs.find((c) => c.id === product.cpoConfig?.id);

    return config ?? null;
  }, [productConfigs]);

  const cpoValue = selectedConfig
    ? getCustomCpoString(resolveLocalizedConfig(selectedConfig, localizedT))
    : "";

  const isApprovalRequired = !!approval;

  return (
    <FlexBox column gap={2}>
      <FlexBox
        alignCenter
        gap={2}
        justifyBetween
        className={classes.listElement}
      >
        <FlexBox maxWidth="70%" gap={1.25} alignCenter fullWidth flex="1">
          <Typography
            variant="h4"
            className={clsx(classes.productInfo, classes.leftPart)}
          >
            {index + 1}.
          </Typography>
          <FlexBox alignCenter gap={1} flex={1} minWidth={0}>
            {isApprovalRequired && (
              <TooltipIcon
                title={t(
                  "exhibitor.cart.payment.product_list.requires_approval"
                )}
                icon={<ImportantIcon className={classes.icon} />}
              />
            )}
            {!!image && (
              <Avatar
                size={32}
                variant="square"
                src={image.url}
                className={classes.avatar}
              />
            )}
            <ElipsisWrapper
              className={classes.productName}
              value={name + cpoValue}
            />
          </FlexBox>
        </FlexBox>
        <FlexBox gap={2} alignCenter maxWidth={180} width={180} flexShrink={0}>
          <Typography variant="h4" className={classes.productInfo}>
            <T
              keyName="exhibitor.cart.payment.product_list.price"
              params={{price: formatPrice(priceWithFees, currency)}}
            />
          </Typography>
          <Typography variant="h4" className={classes.productInfo}>
            <T
              keyName="exhibitor.cart.payment.product_list.quantity"
              params={{count: quantity}}
            />
          </Typography>
        </FlexBox>
      </FlexBox>
      <ProductExtras product={product} currency={currency} />
    </FlexBox>
  );
};

const ProductExtras = ({
  currency,
  product,
}: {
  product: IOrderProduct;
  currency: Currency;
}) => {
  if (!product.extras?.length) {
    return null;
  }
  const extrasString = getExtrasString(product.extras, currency);

  return (
    <Typography variant="h4" style={{color: grey[600]}}>
      <T
        keyName="exhibitor.cart.payment.product_list.extras"
        params={{extras: extrasString}}
      />
    </Typography>
  );
};

export default ProductList;
